import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GenericDataState } from "type";
import ProjectService, { ProjectListQueryParams } from "modules/projects/api/ProjectService";
import { PagedData } from "types";
import { ProjectDetails, ProjectListItem, ProjectDetailSiteContact, PROJECT_STATUS, BASE_PROJECT_TYPE } from "types/project";
import { DataState } from ".";

type SetProjectPayload = {
  projectName: string;
  projectStatus: PROJECT_STATUS;
}

type ProjectDataState = {
  projectsList: GenericDataState<PagedData<ProjectListItem>>;
  current: GenericDataState<ProjectDetails | undefined>;
  sortBy?: string;
  projectSCData?: GenericDataState<ProjectDetailSiteContact>;
  isDevicesTabAvailable?: boolean;
};

const initialState: ProjectDataState = {
  projectsList: {
    dataState: DataState.INITIAL,
    data: {
      items: [],
      totalPages: 0,
      totalCount: 0,
    },
  },
  current: {
    dataState: DataState.INITIAL,
    data: undefined,
  },
};

export const getProjectsList = createAsyncThunk(
  "projects/getProjectsList",
  async (
    params: ProjectListQueryParams,
    thunkApi,
  ) => {
    try {
      const result = await ProjectService.getProjectsList(params);

      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const getProjectData = createAsyncThunk(
  "projects/getProjectData",
  async (
    id: string,
    thunkApi,
  ) => {
    try {
      const result = await ProjectService.getProjectData(id);

      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const getProjectForSC = createAsyncThunk(
  "projects/getProjectForSC",
  async (
    id: string,
    thunkApi,
  ) => {
    try {
      const result = await ProjectService.getProjectForSC(id);

      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const openProject = createAsyncThunk(
  "projects/openProject",
  async (
    params: {
      id: string;
      reopeningReason: string
    },
    thunkApi,
  ) => {
    try {
      const result = await ProjectService.openProject(params.id, { reopeningReason: params.reopeningReason });

      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const checkDevicesTabAvailability = createAsyncThunk(
  "projects/checkDevicesTabAvailability",
  async (
    id: string,
    thunkApi,
  ) => {
    try {
      const result = await ProjectService.isDevicesTabAvailable(id);

      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    setProject: (state, action: PayloadAction<SetProjectPayload>) => {
      if (!state.current || !state.current.data) {
        return;
      }
      state.current.data = {
        ...state.current.data,
        name: action.payload.projectName,
        status: action.payload.projectStatus,
      };
    },
    cleanCurrentProject: (state) => {
      state.current = {
        dataState: DataState.INITIAL,
        data: undefined,
      };
      state.isDevicesTabAvailable = undefined;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    expireDevicesTabAvailability: (state) => {
      state.isDevicesTabAvailable = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProjectsList.pending, (state) => {
      state.projectsList.dataState = DataState.LOADING;
    })
      .addCase(getProjectsList.fulfilled, (state, action) => {
        state.projectsList.dataState = DataState.FULFILLED;
        state.projectsList.data = action.payload;
      })
      .addCase(getProjectsList.rejected, (state) => {
        state.projectsList.dataState = DataState.ERROR;
      })
      .addCase(getProjectData.pending, (state) => {
        state.current = {
          dataState: DataState.LOADING,
        };
      })
      .addCase(getProjectData.fulfilled, (state, action) => {
        state.current = {
          dataState: DataState.FULFILLED,
          data: action.payload,
        };
      })
      .addCase(getProjectData.rejected, (state) => {
        state.current = {
          dataState: DataState.ERROR,
        };
      })
      .addCase(getProjectForSC.pending, (state) => {
        state.projectSCData = {
          dataState: DataState.LOADING,
        };
      })
      .addCase(getProjectForSC.fulfilled, (state, action) => {
        state.projectSCData = {
          dataState: DataState.FULFILLED,
          data: action.payload,
        };
      })
      .addCase(getProjectForSC.rejected, (state) => {
        state.projectSCData = {
          dataState: DataState.ERROR,
        };
      })
      .addCase(checkDevicesTabAvailability.pending, (state) => {
        state.isDevicesTabAvailable = undefined;
      })
      .addCase(checkDevicesTabAvailability.fulfilled, (state, action) => {
        state.isDevicesTabAvailable = action.payload;
      })
      .addCase(openProject.pending, (state) => {
        state.current.dataState = DataState.LOADING;
      })
      .addCase(openProject.rejected, (state) => {
        state.current.dataState = DataState.ERROR;
      })
      .addCase(openProject.fulfilled, (state) => {
        state.current.dataState = DataState.INITIAL;
        state.current = {
          dataState: DataState.INITIAL,
          data: undefined,
        };
      });
  },
});

export default projectsSlice.reducer;

export const selectIsDevicesTabAvailable = (state: { projects: ProjectDataState }) => state.projects.isDevicesTabAvailable;
export const selectProjectsList = (state: { projects: ProjectDataState }) => state.projects.projectsList.data;
export const selectProjectsListState = (state: { projects: ProjectDataState }) => state.projects.projectsList.dataState;
export const selectCurrentProject = (state: { projects: ProjectDataState }) => state.projects.current.data;
export const selectCurrentProjectState = (state: { projects: ProjectDataState }) => state.projects.current.dataState;
export const selectCurrentProjectSC = (state: { projects: ProjectDataState }) => state.projects.projectSCData?.data;
export const selectSortBy = (state: { projects: ProjectDataState }) => state.projects.sortBy;
export const selectCurrentProjectCurrency = (state: { projects: ProjectDataState }) => state.projects.current.data?.currency;
export const selectCurrentProjectLinePercent = (state: { projects: ProjectDataState }) => state.projects.current.data?.linePercent;
export const selectCurrentProjectType = (state: { projects: ProjectDataState }) => state.projects.current.data?.projectType.value;
export const selectCurrentProjectStatus = (state: { projects: ProjectDataState }) => state.projects.current.data?.status;
export const selectCurrentPrimaryRiskEngineer = (state: { projects: ProjectDataState }) => state.projects.current.data?.primaryRiskEngineer;
export const selectCurrentPrimarySiteContact = (state: { projects: ProjectDataState }) => state.projects.current.data?.primarySiteContact;
export const selectCurrentIsPrimarySiteContact = (state: { projects: ProjectDataState }) => state.projects.projectSCData?.data?.isPrimarySiteContact;
export const selectCanUploadFiles = (state: { projects: ProjectDataState }) => state.projects.projectSCData?.data?.canUploadDocuments ?? true;
export const selectRiskScoreMax = (state: { projects: ProjectDataState }) => state.projects.current.data?.riskScoreMax;
export const selectIsProjectClosed = (state: { projects: ProjectDataState }) => {
  let tempStatus = state.projects.current.data?.status === PROJECT_STATUS.CLOSED;

  if (!tempStatus) {
    tempStatus = state.projects.projectSCData?.data?.status === PROJECT_STATUS.CLOSED;
  }

  return tempStatus;
};
export const selectDisclaimerCompanyName = (state: { projects: ProjectDataState }) => state.projects.current.data?.disclaimerCompanyName;
export const selectProjectPremiumValues = (state: { projects: ProjectDataState }) => {
  const { brPremium, dsuPremium, triaPremium, type, biPremium } = state.projects.current.data ?? {};

  if (type === BASE_PROJECT_TYPE.OPERATIONAL_PROPERTY) {
    return { biPremium: biPremium || 1 };
  }

  return { brPremium, dsuPremium, triaPremium };
};
export const selectIsOperationalProperty = (state: { projects: ProjectDataState }) => {
  if (state.projects.current.data == null && state.projects.projectSCData?.data !== null) {
    return state.projects.projectSCData?.data?.type === BASE_PROJECT_TYPE.OPERATIONAL_PROPERTY;
  }
  const { type } = state.projects.current.data ?? {};

  return type === BASE_PROJECT_TYPE.OPERATIONAL_PROPERTY;
};

export const { setSortBy, setProject, cleanCurrentProject } = projectsSlice.actions;
